/**
 * <newsletter-popup> — the first-visit newsletter sheet (SAVETTE-70; markup in
 * snippets/newsletter-popup.liquid).
 *
 * As on the headless site: it opens a second after the page loads on a browser without the
 * savette_newsletter_popup cookie, and closing it (the cross, the mask, Escape) or signing up
 * sets that cookie for the number of days the footer section sets (7 by default), path=/,
 * SameSite=Lax. The cookie name is the headless site's, so a visitor who closed it there is not
 * asked again.
 *
 * It is a passive sheet, not a modal: it never takes focus when it opens and does not trap it.
 * If focus is inside it when it closes, focus goes back to where it came from (or to the main
 * content), so it is never lost on a hidden element.
 *
 * Signing up is the native form post, as in the footer. The page that comes back carries the
 * form's state (success or error) and, in its address, the id of the form that was sent. Shopify
 * renders a posted state into every customer form on the page, so the id decides where it shows:
 * this sheet reopens at once with its thank-you or its error only when it was the one submitted.
 * A sign-up from either form sets the cookie.
 *
 * While Shopify's cookie banner is waiting for the visitor's choice, the sheet waits for that
 * choice too, then opens a second later, so the two never stack on a first visit.
 *
 * In the theme editor it never opens by itself; it shows while the footer section is selected,
 * so the merchant can see their settings, and closing it there sets no cookie.
 */
const COOKIE = 'savette_newsletter_popup';
const OPEN_DELAY_MS = 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** How long to wait for Shopify's privacy library before opening anyway: 10 checks, 500ms apart. */
const CONSENT_CHECKS = 10;
const CONSENT_CHECK_MS = 500;

// The footer section selected in the theme editor. Module state outlives the section being
// re-rendered on a settings change, so the preview stays open while the merchant edits.
let editorSelectedSection = null;

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('focusin', this.onFocusIn);
    document.addEventListener('keydown', this.onDocumentKeydown);

    if (this.designMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (editorSelectedSection === this.sectionId) this.open();
      return;
    }

    this.start();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('focusin', this.onFocusIn);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.removeEventListener('visitorConsentCollected', this.onConsentCollected);
    clearTimeout(this.timer);
  }

  get designMode() {
    return this.hasAttribute('data-design-mode');
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  start() {
    const state = this.querySelector('[data-newsletter-popup-state]')?.dataset.newsletterPopupState;
    const submittedHere = window.location.hash === `#${this.dataset.formId}`;

    if (state === 'success') {
      this.remember();
      if (submittedHere) {
        this.open();
        // the thank-you was in the page from the start, so a screen reader would not hear it: take
        // focus to it once the page has landed
        this.whenLoaded(() => this.focusStatus());
      }
      return;
    }
    if (state === 'error') {
      // the visitor is mid sign-up: show the error where they typed, and put them back in the field
      if (submittedHere) {
        this.open();
        this.whenLoaded(() => this.querySelector('input[type="email"]')?.focus({ preventScroll: true }));
      }
      return;
    }
    if (this.hasCookie()) return;

    this.timer = setTimeout(this.openAfterConsent, OPEN_DELAY_MS);
  }

  /** Runs after the page has loaded, once the browser has finished landing on the address's #id. */
  whenLoaded(callback) {
    if (document.readyState === 'complete') callback();
    else window.addEventListener('load', callback, { once: true });
  }

  openAfterConsent = () => {
    // Shopify's privacy library can arrive after the pop-up is due; give it a few seconds, so the
    // sheet never opens under a banner that has not appeared yet. A store with no banner never
    // loads it, and the sheet opens once the wait is over.
    if (!this.privacyLoaded() && (this.consentChecks = (this.consentChecks ?? 0) + 1) <= CONSENT_CHECKS) {
      this.timer = setTimeout(this.openAfterConsent, CONSENT_CHECK_MS);
      return;
    }
    if (this.consentPending()) {
      document.addEventListener('visitorConsentCollected', this.onConsentCollected, { once: true });
      return;
    }
    this.open();
  };

  privacyLoaded() {
    return typeof window.Shopify?.customerPrivacy?.shouldShowBanner === 'function';
  }

  focusStatus() {
    const status = this.querySelector('[role="status"]');
    if (!status) return;
    if (!status.hasAttribute('tabindex')) status.setAttribute('tabindex', '-1');
    status.focus({ preventScroll: true });
  }

  onConsentCollected = () => {
    this.timer = setTimeout(() => this.open(), OPEN_DELAY_MS);
  };

  /** Shopify's banner is showing and the visitor has not answered it yet. */
  consentPending() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.shouldShowBanner !== 'function' || !privacy.shouldShowBanner()) return false;
      const consent = privacy.currentVisitorConsent?.() ?? {};
      return !Object.values(consent).some((value) => value === 'yes' || value === 'no');
    } catch {
      return false;
    }
  }

  open() {
    this.setAttribute('data-open', '');
  }

  close() {
    if (!this.isOpen) return;
    const focusInside = this.contains(document.activeElement);
    this.removeAttribute('data-open');
    this.remember();
    if (focusInside) this.restoreFocus();
  }

  restoreFocus() {
    const target =
      this.focusOrigin?.isConnected && !this.contains(this.focusOrigin)
        ? this.focusOrigin
        : document.getElementById('MainContent');
    target?.focus({ preventScroll: true });
  }

  remember() {
    if (this.designMode) return;
    const days = Number(this.dataset.days) || 7;
    const expires = new Date(Date.now() + days * DAY_MS).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE}=true; expires=${expires}; path=/; SameSite=Lax${secure}`;
  }

  hasCookie() {
    return document.cookie.split('; ').some((row) => row.startsWith(`${COOKIE}=`));
  }

  onClick = (event) => {
    if (event.target.closest('[data-newsletter-popup-close]')) this.close();
  };

  onSubmit = () => {
    // the native post goes ahead; the returned page shows the result
    this.remember();
  };

  onFocusIn = (event) => {
    const from = event.relatedTarget;
    if (from instanceof HTMLElement && !this.contains(from)) this.focusOrigin = from;
  };

  onDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen || event.defaultPrevented) return;
    // Escape belongs to whatever has focus: the pop-up takes it only from inside itself or from the
    // page at large, so closing the menu, a filter panel or a drawer leaves the sheet alone
    const target = event.target instanceof Element ? event.target : null;
    const fromPage = !target || target === document.body || target === document.documentElement || target.id === 'MainContent';
    if (!fromPage && !this.contains(target)) return;
    this.close();
  };

  onSectionSelect = (event) => {
    editorSelectedSection = event.detail?.sectionId ?? null;
    if (editorSelectedSection === this.sectionId) this.open();
  };

  onSectionDeselect = (event) => {
    if (event.detail?.sectionId !== this.sectionId) return;
    editorSelectedSection = null;
    this.removeAttribute('data-open');
  };
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
